import { useState, useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import _ from 'lodash';

import { Alert, Button, Table, Skeleton, Collapse, Divider, Tag } from 'antd';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Title from 'antd/lib/typography/Title';
import { CheckSquareOutlined, PrinterOutlined } from '@ant-design/icons';

import { AuthModal, ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { formatPhoneNumber, handleDownload, toLetters, getDisplayCompanyId } from 'utils/helpers';
import { Color, StatusCode } from 'utils/enums';
import { diagRefCode, H5499_POS_CODES } from 'utils/objects';

import { AuthDiags, getPortalAuth } from 'store/authorization/portal/actions';
import { getAllDesc as getAllIpasDesc } from 'store/app/ipa/actions';
import { getDownload as downloadResources, getDownloadableResources } from 'store/app/actions';
import { getAttachInfo, downloadAttachment } from 'store/authorization/attach/actions';
import { get as getRuleCodes } from 'store/authorization/ruleCodes/actions';
import { getLetterInfo } from 'store/authorization/letter/actions';

import { CreateAuthorizationConfirmationProps } from './Proptypes';
import { LetterInfo } from 'components/Auth/Detail/Proptypes';

import styles from './CreateAuthorizationConfirmation.module.scss';

// APIs
/*

AUTHORIZATION CONFIRMATION PAGE
  (init)
  - AuthMember/AuthCreateUploadFiles
  - AuthMember/ReferTypes
  - AuthMember/RuleCodes?SetName=SHOW%20VACCINE%20FORM
  - AuthMember/GetPortalAuth?authNo=20210907799998700001&companyId=APC
  - AuthMember/GetAuthInstructions?companyId=APC
  - App/GetDownloadableResources?companyId=APC&materialType=FORM&measureCode=Auth
  - AuthMember/GetAuthAttach?authNo=20210907799998700001&companyId=APC

*/

const CreateAuthorizationConfirmation = (props: CreateAuthorizationConfirmationProps) => {

  const history = useHistory();
  const location = useLocation();

  const [portalAuthMaster, setPortalAuthMaster] = useState<any>({});
  const [portalDiagSource, setPortalDiagSource] = useState<any[]>([]);
  const [portalDetailSource, setPortalDetailSource] = useState<any[]>([]);
  const [attachmentList, setAttachmentList] = useState<any[]>([]);
  const [referType, setReferType] = useState('');
  const [showSymptoms, setShowSymptoms] = useState(false);
  const [authStatus, setAuthStatus] = useState('');
  const [ipaFullName, setIpaFullName] = useState('');
  const [downloadableForms, setDownloadableForms] = useState<any[]>([]);
  const [jCodes, setJCodes] = useState<any[]>([]);
  const [rXFormVisible, setRXFormVisible] = useState(false);
  const [showHospitalDates, setShowHospitalDates] = useState(false);
  const [modifyAuthReferralBy, setModifyAuthReferralBy] = useState<any>([]);

  const [authDetailState, setAuthDetailState] = useState({
    authNo: '',
    healthPlanAuthNumber: '',
    requestDate: '',
    referenceType: '',
    certificateType: '',
    actionDate: '',
    createdDate: '',
    expirationDate: '',
    retroDate: '',
    companyId: '',
    status: '',
    statusNumber: '',
    memberName: '',
    memberPhoneNumber: '',
    healthPlan: '',
    dateOfBirth: '',
    gender: '',
    memberId: '',
    memberPcp: '',
    referralBy: '',
    referralByNumber: '',
    referralTo: '',
    referralSpecialty: '',
    referralPos: '',
    referralByFaxNumber: '',
    memberAddress: '',
    memberCity: '',
    memberState: '',
    memberZipCode: '',
    referralToAddress: '',
    referralToCity: '',
    referralToState: '',
    referralToZipCode: '',
    referralToPhoneNumber: '',
    referralToFaxNumber: '',
    facilityName: '',
    facilityAddress: '',
    facilityCity: '',
    facilityState: '',
    facilityZipCode: '',
    facilityPhoneNumber: '',
    authDiagsData: [{}],
    authDetailsData: [{}],
    authNotesData: [{}],
    formColumnData: [{}],
    letterInfos: [{}],
    showNotes: false,
    attachments: [{}],
  } as AuthModal);

  useEffect(() => {
    getPortalAuth();
    getAuthAttach();
    getLetterInfo();
    getAllIpasDesc();
    getDownloadableResources();
    getRuleCodes('JCODE%20FORM%20CPT', setJCodes);
  }, []);

  const diagColumns = [
    {
      title: 'REFERENCE',
      key: 'index',
      dataIndex: 'index',
      render: (_: string, __: any, index: number) => toLetters(index + 1),
    },
    {
      title: 'DIAGNOSIS CODE',
      key: 'RmiDiag',
      dataIndex: 'RmiDiag',
    },
    {
      title: 'DESCRIPTION',
      key: 'RmiDiagDesc',
      dataIndex: 'RmiDiagDesc',
    },
  ];

  const cptColumns = [
    {
      title: 'CPT CODE',
      key: 'RmiProcCode',
      dataIndex: 'RmiProcCode',
    },
    {
      title: 'DESCRIPTION',
      key: 'RmiDescr',
      dataIndex: 'RmiDescr',
    },
    {
      title: 'MODIFIER',
      key: 'RmiModif',
      dataIndex: 'RmiModif',
      render: (_: string, record: any, __: number) => record.RmiModif?.join(' ')
    },
    {
      title: 'DIAG REF',
      key: 'RmiDiagCode',
      dataIndex: 'RmiDiagCode',
    },
    {
      title: 'QTY',
      key: 'RmiQty',
      dataIndex: 'RmiQty',
    },
  ];

  const getPortalAuth = () => {
    return props.getPortalAuth(props.authNumber.authNumber, props.authNumber.companyId, parseInt(sessionStorage.getItem('user_role')!) )
      .then((response: any) => {

        const authDetailsData: any = [];
        const authDiagsData: any = [];
        const authNotesData: any = [];
        const portalDetailSource: any = [];

        // Need to split this into array because the API doesn't provide first and last name for these fields
        const memberFullName = response.RmiMembName.split(',');
        const authPcpFullName = response.RmiPcpRevFullName.split(',');
        const requestProviderFullName = response.RmiReqRevFullName.split(',');

        const groupCptCodes = _.mapValues(_.groupBy(response.AuthDetails, 'RmiProcCode'));
        // Group the modifiers in the cpt code table
        for (const cptCode of Object.values(groupCptCodes)) {
          if (cptCode.length > 0) {
            portalDetailSource.push({
              RmiProcCode: cptCode[0].RmiProcCode,
              RmiDescr: cptCode[0].RmiDescr,
              RmiModif: cptCode.map((x: any) => (x.RmiModif)),
              RmiDiagCode: cptCode[0].RmiDiagCode,
              RmiQty: cptCode[0].RmiQty
            });
          } else {
            portalDetailSource.push({
              RmiProcCode: cptCode[0].RmiProcCode,
              RmiDescr: cptCode[0].RmiDescr,
              RmiModif: cptCode[0].RmiModif,
              RmiDiagCode: cptCode[0].RmiDiagCode,
              RmiQty: cptCode[0].RmiQty
            });
          }
        }

        checkHospitalDates(response);

        setPortalAuthMaster(response);
        setReferType(response.AuthDiags.find((item: AuthDiags) => diagRefCode.includes(item.RmiDiag))?.RmiDiagDesc);
        setPortalDiagSource(response.AuthDiags.filter((item: AuthDiags) => !diagRefCode.includes(item.RmiDiag)));
        setAuthStatus(StatusCode[parseInt(response.RmiStatus)].toUpperCase());
        setPortalDetailSource(portalDetailSource);


        // From the legacy code, it shows there are 6 different dialog types that are mixed inside AuthDiags response
        // This is used to populate the modify auth flow later (diagnosis section)
        for(const key in response.AuthDiags) {
          if(response.AuthDiags[key].RmiDiag !== 'RR' && response.AuthDiags[key].RmiDiag !== 'MR' && response.AuthDiags[key].RmiDiag !== 'RA' && response.AuthDiags[key].RmiDiag !== 'DR' && response.AuthDiags[key].RmiDiag !== 'UR' && response.AuthDiags[key].RmiDiag !== 'PR') {
            authDiagsData.push({
              key: key,
              ref: response.AuthDiags[key].RmiDiagRefNo,
              diagCode: response.AuthDiags[key].RmiDiag,
              desc: response.AuthDiags[key].RmiDiagDesc
            });
          }
        }

        // This is used to populate the modify auth flow later (cpt code section)
        for(const key in response.AuthDetails) {
          authDetailsData.push({
            key: key,
            cpt: response.AuthDetails[key].RmiProcCode,
            desc: response.AuthDetails[key].RmiDescr,
            mod: response.AuthDetails[key].RmiModif,
            diagRef: response.AuthDetails[key].RmiDiagCode,
            quantity: response.AuthDetails[key].RmiQty
          });
        }

        // This is used to populate the modify auth flow later (Symptoms and Treatments section)
        for(const key in response.AuthNotes) {
          authNotesData.push({
            key: key,
            subject: response.AuthNotes[key].RmiSubject,
            notes: response.AuthNotes[key].RmiNotes
          });
        }

        // This is used to populate the modify auth flow later (all the data needed to populate the create auth detail)
        setAuthDetailState((previousState: AuthModal) => ({
          ...previousState,
          authNo: response.RmiAuthNo,
          healthPlanAuthNumber: response.RmiHpAuthNo,
          requestDate: response.RmiReqDate,
          referenceType: response.AuthDiags.find((item: AuthDiags) => diagRefCode.includes(item.RmiDiag))?.RmiDiagDesc,
          certificateType: response.RmiCertTypeDesc,
          actionDate: response.RmiAuthDate,
          createdDate: response.RmiCreatedDate,
          expirationDate: response.RmiExprDate,
          retroDate: response?.RmiRetroDate ? response.RmiRetroDate : response.RmiAuthDate,
          companyId: response.RmiCompanyId,
          status: StatusCode[response.RmiStatus].toUpperCase(),
          statusNumber: response.RmiStatus,
          memberName: response.RmiMembName,
          memberPhoneNumber: formatPhoneNumber(response.RmiMembaddrPhone),
          healthPlan: response.RmiHpName,
          dateOfBirth: response.RmiBirth,
          gender: response.RmiSex,
          memberId: response.RmiMembId,
          membKeyId: response.RmiMembKeyId,
          memberPcp: response.RmiPcpRevFullName,
          primPcpKeyId: response.RmiPrimPcpKeyId,
          referralBy: response.RmiRefRevFullName,
          referralByNumber: formatPhoneNumber(response.RmiReferByPhone),
          referralTo: response.RmiReqRevFullName,
          referralSpecialty: response.RmiReqSpec,
          referralPos: response.RmiPlaceSvc,
          referralByFaxNumber: formatPhoneNumber(response.RmiReferByFax),
          memberAddress: response.RmiMembaddrStreet,
          memberCity: response.RmiMembaddrCity,
          memberState: response.RmiMembaddrState,
          memberZipCode: response.RmiMembaddrZip,
          referralToAddress: response.RmiSpecaddrStreet,
          referralToCity: response.RmiSpecaddrCity,
          referralToState: response.RmiSpecAddrState,
          referralToZipCode: response.RmiSpecaddrZip,
          referralToPhoneNumber: formatPhoneNumber(response.RmiSpecaddrPhone),
          referralToFaxNumber: formatPhoneNumber(response.RmiSpecaddrFax),
          facilityName: response.FANAME,
          facilityAddress: response.FASTREET,
          facilityCity: response.FACITY,
          facilityState: response.FASTATE,
          facilityZipCode: response.FAZIP,
          facilityPhoneNumber: formatPhoneNumber(response.FAPHONE),
          authDiagsData: authDiagsData,
          authDetailsData: authDetailsData,
          authNotesData: authNotesData,
          authPcpId: response.RmiAuthPcp,
          authPcpKeyId: response.RmiAuthPcpKeyId,
          requestProv: response.RmiReqProv,
          reqProvKeyId: response.RmiReqProvKeyId,
          authPcpFirstName: authPcpFullName[1],
          authPcpLastName: authPcpFullName[0],
          requestProviderFirstName: requestProviderFullName[1],
          requestProviderLastName: requestProviderFullName[0],
          memberFirstName: memberFullName[1],
          memberLastName: memberFullName[0],
          priorityStatus: response.RmiPriorityStatus,
          pos: response.RmiPOS,
          rmiAdmitDate: response.RmiAdmitDate,
          rmiDschDate: response.RmiDschDate,
          rmiDischDisp: response.rmiDischDisp
        }));

        // MedicoRX check
        if (response.RmiReqRevFullName && response.RmiReqRevFullName?.toLowerCase().includes('medicorx specialty') && ['ADV', 'AHC', 'APC', 'GOM', 'AVISTA', 'CVIPA', 'BAIPA', 'GSGP'].includes(props.authNumber.companyId)) {
          setRXFormVisible(true);
        } else setRXFormVisible(false);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getAuthAttach = () => {
    return props.getAttachInfo(props.authNumber.authNumber, props.authNumber.companyId)
      .then((response: any[]) => {
        const attachmentArray: any = [];
        for (const key in response) {
          attachmentArray.push({
            key: key,
            authNo: response[key][0],
            companyId: response[key][1],
            folderLocation: response[key][2],
            fileName: response[key][3],
            fileMeta: response[key][4],
          });
        }
        setAttachmentList(attachmentArray);
        setAuthDetailState( (previous: any) => ({ ...previous, attachments: attachmentArray }) );
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getLetterInfo = () => {
    props.getLetterInfo(props.authNumber.authNumber, props.authNumber.companyId)
      .then((info: LetterInfo) => {
        const letterInfosArray: Array<Object> = [];
        for (const key in info) {
          if (info[key]['RmiFileName'].indexOf('APPROVAL_LETTER') >= 0 && info[key]['RmiFileName'].indexOf('PROVIDER') === -1)  {
            continue;
          }

          letterInfosArray.push({
            key: key,
            fileName: info[key]['RmiFileName'],
            fileLocation: info[key]['RmiFileLocation'],
            fileDate: info[key].RmiFileDate
          });
        }

        setAuthDetailState((previousState: AuthModal) => ({
          ...previousState,
          letterInfos: letterInfosArray
        }));

      }).catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });

  }

  const getAllIpasDesc = () => {
    return props.getAllIpasDesc()
      .then((response: any[]) => {
        setIpaFullName(response.find(ipa => ipa.RmiCompanyId === props.authNumber.companyId).RmiCompanyFullName);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getDownloadableResources = () => {
    props.getDownloadableResources('Form', 'Auth', props.authNumber.companyId)
    .then((formData: any[]) => {
      setDownloadableForms(formData);
    }).catch((err: ErrorResponse) => {
      notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
    });
  };

  const downloadPDF = (filePath: string) => {
    props.downloadResources(filePath)
      .then((response: string) => {
        let blob = new Blob([response], {type: "application/pdf"});
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, "_blank", "");
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const downloadAttachment = (location: string, filename: string, fileMeta: string) => {
    const downloadAttachmentParams = {
      AuthNo: props.authNumber.authNumber,
      CompanyId: props.authNumber.companyId,
      FileLocation: location,
      FileName: filename,
    };
    props.downloadAttachment(downloadAttachmentParams)
      .then((response: any) => {
        handleDownload(response, fileMeta);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getRuleCodes = (setName: string, setFunc: Function) => {
    return props.getRuleCodes(setName)
      .then((response: string[]) => {
        setFunc(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const onPrint = () => {
    window.print();
  };

  const panelHeader = (
    <div>
      <Title
        className="print-title"
        level={5}
        style={{
          color: 'white',
          fontWeight: 'bolder',
          textAlign: 'center',
        }}
      >
        Authorization request has been submitted.
      </Title>
    </div>
  );

  const checkHospitalDates = (authData: any) => {
    let H5499Found = false;
    if (authData.RmiPOS && H5499_POS_CODES.includes(authData.RmiPOS.trim())) {
        H5499Found = authData.AuthDetails.some((detail: any) => detail.RmiProcCode && detail.RmiProcCode.trim() === "H5499");

        //03/17/17 changed spec per meeting with CM, UM, and Dr. Wang
        if (authData.RmiAuthDate && !H5499Found) {
            setShowHospitalDates(false);
        }
        else if (H5499Found && authData.RmiSvcType !== "65") {
            setShowHospitalDates(true);
            authData.AuthDetails.forEach((detail: any) => {
              if (detail.RmiProcCode != null) {
                var procCode = detail.RmiProcCode;
                //get admit and discharge date from detail line
                if (procCode.trim() === "H5499") {
                  authData.RmiAdmitDate = detail.RmiAdmitDate;
                  authData.RmiDschDate = detail.RmiDschDate;
                  authData.RmiDischDisp = authData.RmiDischDisp;
                }
              }
            })
        }
    } else {
        setShowHospitalDates(false);
    }
  };

  return (
    <>

    {props.isFetchingPortalAuth && (
      <>
       <Skeleton
         active
         paragraph={{ rows: 4 }}
       />
       <br />
       <Skeleton
         active
         paragraph={{ rows: 4 }}
       />
      </>
    )}

    {!props.isFetchingPortalAuth && (
    <div className={styles['print-auth-info']}>
      <Collapse
        className="mt-3"
        collapsible={'disabled'}
        defaultActiveKey={['1']}
      >
        <Collapse.Panel
          header={panelHeader}
          key="1"
          className="panel-header"
          showArrow={false}
        >
          <div className={`col-lg-24 mt-2 mb-1 ${styles['no-print']}`}>
            <div className="btn-wrapper">
              <Button type="primary" className={`mr-1 ${styles['full-width-button']}`} onClick={() => history.push('/quality/overview')}>
                <CheckSquareOutlined /> Done
              </Button>

              {(location.pathname === '/auth/create') ?
                <Button type="primary" className={`mr-1 ${styles['full-width-button']}`} onClick={() => props.setPage('create-authorization-page') }>
                  Another Auth for Same Member
                </Button> : null
              }

              <Button type="primary" className={`mr-1 ${styles['full-width-button']}`} onClick={() => location.pathname === '/auth/create' ? props.setPage('start') :
                history.push({
                  pathname: '/auth/search',
                  state: {
                    previousPath: location.pathname, // '/auth/create' or '/auth/modify-auth'
                  }
                })
              }>
                {location.pathname === '/auth/create' ? 'Another Auth for Different Member' : 'Modify Another Auth'}
              </Button>

              <Button type="primary" className={styles['full-width-button']} onClick={onPrint}>
                <PrinterOutlined /> Print
              </Button>
            </div>
          </div>

          <div className={`d-flex justify-content-end ${styles['no-print']}`}>
            <Checkbox
              checked={showSymptoms}
              onChange={e => setShowSymptoms(e.target.checked)}
            />&nbsp;&nbsp;<span>Check to show and include "Symptoms and Treatments" in printout</span>
          </div>

          <div className="d-flex justify-content-center mt-1 mb-1">
            <Alert
              className={styles['print-section']}
              type="error"
              message={`If additional documents need to be submitted. Please print this confirmation as cover page and fax to 626-442-1106.`}
            />
          </div>

          <div className={`row mt-3 ${styles['print-section']}`}>
            <div className={`col-lg-12 ${styles['col-print-6']}`}>
              <table className="test" style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={1}>
                    <th>Auth Number:</th>
                    <td>{getDisplayCompanyId(portalAuthMaster.RmiCompanyId)} - {portalAuthMaster.RmiAuthNo}</td>
                  </tr>

                  {showHospitalDates ? (
                    <>
                      <tr>
                        <th>Admit Date:</th>
                        <td>{portalAuthMaster.RmiAdmitDate ? moment(portalAuthMaster.RmiAdmitDate).format('MM/DD/YYYY') : ''}</td>
                      </tr>
                    </>
                  ) :
                  (<>
                  <tr key={2}>
                    <th>Request Date:</th>
                    <td>{portalAuthMaster.RmiReqDate ? moment(portalAuthMaster.RmiReqDate).format('MM/DD/YYYY') : ''}</td>
                  </tr>
                  <tr key={3}>
                    <th>Referral Type:</th>
                    <td>{referType}</td>
                  </tr>
                  </>)}
                </tbody>
              </table>
            </div>
            <div className={`col-lg-12 ${styles['col-print-6']}`}>
              <table className="test" style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={4}>
                    <th>Status:</th>
                    <td>
                      { authStatus === 'APPROVED' && <Tag color={Color.APPROVED}>APPROVED</Tag> }
                      { authStatus === 'MODIFIED' && <Tag color={Color.MODIFIED}>MODIFIED</Tag> }
                      { authStatus === 'DEFERRED' && <Tag color={Color.DEFERRED}>DEFERRED</Tag> }
                      { authStatus === 'CANCELLED' && <Tag color={Color.CANCELLED}>CANCELLED</Tag> }
                      { authStatus === 'EXPIRED' && <Tag color={Color.EXPIRED}>EXPIRED</Tag> }
                      { authStatus === 'DENIED' && <Tag color={Color.DENIED}>DENIED</Tag>}
                      { authStatus === 'PENDED' && <Tag color={Color.PENDED}>PENDED</Tag>}
                      { authStatus === 'REQUESTED' && <Tag style={{ "color": Color.REQUESTED_TEXT}}  color={Color.REQUESTED}>REQUESTED</Tag> }
                      { authStatus === 'SYSTEM_HOLD' && <Tag style={{ "color": Color.SYSTEM_HOLD_TEXT}} color={Color.SYSTEM_HOLD}>SYSTEM HOLD</Tag> }
                    </td>
                  </tr>
                  {showHospitalDates ? (
                    <>
                      <tr>
                        <th>Discharge Date:</th>
                        <td>{portalAuthMaster.RmiDschDate ? moment(portalAuthMaster.RmiDschDate).format('MM/DD/YYYY') :  ''}</td>
                      </tr>
                    </>
                    ) : (
                    <>
                      <tr>
                        <th>Expiration Date:</th>
                        <td>{portalAuthMaster.RmiExprDate ? moment(portalAuthMaster.RmiExprDate).format('MM/DD/YYYY') : ''}</td>
                      </tr>
                    </>)
                  }
                </tbody>
              </table>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`}/>

          <div className={`row ${styles['print-section']}`}>
            <div className={`col-lg-12 ${styles['col-print-6']}`}>
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th>Patient Name:</th>
                    <td>
                      {portalAuthMaster.RmiMemRevFullName}
                      <br />
                      {portalAuthMaster.RmiMembaddrStreet}
                      {portalAuthMaster.RmiMembaddrStreet2 ? <><br/>{portalAuthMaster.RmiMembaddrStreet2}</> : null}
                    </td>
                  </tr>
                  <tr>
                    <th>Health Plan:</th>
                    <td>{portalAuthMaster.RmiHpName}</td>
                  </tr>
                  <tr>
                    <th>Member ID:</th>
                    <td>{portalAuthMaster.RmiMembId}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={`col-lg-12 col-print-6 ${styles['print-section']}`}>
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th>Date of Birth:</th>
                    <td>
                      {moment(portalAuthMaster.RmiBirth).format('MM/DD/YYYY')}
                    </td>
                  </tr>
                  <tr>
                    <th>Gender: </th>
                    <td>{portalAuthMaster.RmiSex}</td>
                  </tr>
                  <tr>
                    <th>Phone Number:</th>
                    <td>{formatPhoneNumber(portalAuthMaster.RmiMembaddrPhone)}</td>
                  </tr>
                  <tr>
                    <th>Member PCP:</th>
                    <td>{portalAuthMaster.RmiPcpRevFullName}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`} />

          <div className="row">
            <div className={`col-lg-12 col-print-6 ${styles['print-section']}`}>
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th>Request Provider:</th>
                    <td>
                      {portalAuthMaster.RmiRefRevFullName}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`} />

          <div className="row">
            <div className={`col-lg-12 col-print-6 ${styles['print-section']}`}>
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th>Referral To:</th>
                    <td>{portalAuthMaster.RmiReqRevFullName}</td>
                  </tr>
                  <tr>
                    <th>Specialty:</th>
                    <td>{portalAuthMaster.RmiReqSpec}</td>
                  </tr>
                  <tr>
                    <th>POS:</th>
                    <td>{portalAuthMaster.RmiPlaceSvc}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={`col-lg-12 ${styles['col-print-6']} ${styles['print-section']}`}>
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th>Address:</th>
                    <td>
                    {portalAuthMaster.RmiSpecaddrStreet}
                    {portalAuthMaster.RmiSpecaddrStreet2 ? <><br/>{portalAuthMaster.RmiSpecaddrStreet2}</> : null}
                    <br />
                    {portalAuthMaster.RmiSpecaddrCity}, {portalAuthMaster.RmiSpecAddrState} {portalAuthMaster.RmiSpecaddrZip}
                    </td>
                  </tr>
                  <tr>
                    <th>Phone Number:</th>
                    <td>{formatPhoneNumber(portalAuthMaster.RmiSpecaddrPhone)}</td>
                  </tr>
                  <tr>
                    <th>Fax Number:</th>
                    <td>{formatPhoneNumber(portalAuthMaster.RmiSpecaddrFax)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`} />

          <Table
            className={styles['diag-table']}
            columns={diagColumns}
            dataSource={portalDiagSource}
            rowKey={'index'}
            pagination={false}
          />

          <br />

          <Table
            className={styles['cpt-table']}
            columns={cptColumns}
            dataSource={portalDetailSource}
            rowKey={'RmiProcCode'}
            pagination={false}
          />

          {rXFormVisible ? (
          <>
            <div className={styles['no-print']}>
              {downloadableForms.map(val => {
                if (val.RmiFileDesc === 'MedicoRX Prescription Request Form') {
                  return <a href='#/' onClick={() => downloadPDF(val.RmiLocationPath + val.RmiFileName)}>Download {val.RmiFileDesc}</a>;
                }
              })}

            </div>

            <Divider className={`${styles['print-ant-divider-horizontal']}`}/>
          </>
          ) : (
            <br />
          )}

          {portalDetailSource.find(x => jCodes.includes(x.RmiProcCode?.toUpperCase().trim())) && (
            <>
              <div className={styles['no-print']}>
                {downloadableForms.map(val => {
                  if (val.RmiFileDesc === "Prescription Drug Prior Authorization Form") {
                    return <a href='#/' onClick={() => downloadPDF(val.RmiLocationPath + val.RmiFileName)}>Download Prescription Drug Form</a>;
                  }
                })}

              </div>

              <Divider className={`${styles['print-ant-divider-horizontal']}`}/>
            </>
          )}

          {showSymptoms && (
            <>
              <Title level={5}>
                Symptoms and Treatments
              </Title>
              <hr />
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {portalAuthMaster.AuthNotes[0].RmiNotes.split('\r\n')[0]}<br />{'Submitted by ' + portalAuthMaster.AuthNotes[0].RmiNotes.split('\r\n')[1] + ' on ' + moment(portalAuthMaster.AuthNotes[0].RmiNotes.split('\r\n')[2]).format('ddd, MMM DD YYYY [at] HH:mm:ss')}
              </div>
              <hr />
            </>
          )}

          <Title className={styles['print-section']} level={5}>
            Attachment:
          </Title>

          <ul>
            {attachmentList.map((data: any, key: number) => <li key={key}><a href='#/' onClick={() => downloadAttachment(data.folderLocation, data.fileName, data.fileMeta)}>{data.fileMeta}</a></li>)}
          </ul>

          <Divider className={`${styles['print-ant-divider-horizontal']}`}/>

          <Alert
            message=""
            description={
              <>
                Eligibility must be verified 24 hours before the actual date of service.
                Provider MUST confirm with Health Plan/IPA that the member&#8217;s health plan coverage is still in effect. {getDisplayCompanyId(portalAuthMaster.RmiCompanyId)} reserves
                the right to revoke this authorization prior to services being rendered based on cancellation of the member&#8217;s eligibility.
                Final determination of benefits will be made after review of the claim for limitations or exclusions.
              </>
             }
          />

          <div className={`row mt-2 ${styles['print-section']}`}>
            <div className={`col-lg-12 ${styles['col-print-6']}`}>
              REF#: {getDisplayCompanyId(portalAuthMaster.RmiCompanyId)}-{portalAuthMaster.RmiAuthNo}:{portalAuthMaster.RmiMembId}
            </div>

            <div className={`col-lg-12 d-flex justify-content-end ${styles['col-print-6']}`}>
              {moment().format("MM/DD/YYYY [at] h:mmA")}
            </div>
          </div>

        </Collapse.Panel>
      </Collapse>
    </div>
    )}

    </>
  );
};
const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingPortalAuth: store.rootReducer.authPortal.isFetching,
  isFetchingIpaList: store.rootReducer.appIPA.isFetchingAllDesc,
  isFetchingResources: store.rootReducer.app.isFetchingResources,
  isDownloadingResources: store.rootReducer.app.isFetchingFile,
  isFetchingAttachInfo: store.rootReducer.authAttach.isFetchingInfo,
  isDownloadingAttach: store.rootReducer.authAttach.isDwonloading,
  isFetchingRuleCodes: store.rootReducer.authRuleCodes.isFetching,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getPortalAuth,
    getAllIpasDesc,
    getDownloadableResources,
    downloadResources,
    getAttachInfo,
    getLetterInfo,
    downloadAttachment,
    getRuleCodes,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(CreateAuthorizationConfirmation);